import uuid from "uuid-random";
import { Gate } from "../../common/windowFunctions";
import { safeInteropRegister } from "../../common/windowFunctions";
import { logStatus } from "../../utils";
import { toGoLang } from "../../common/windowFunctions";
import { PromiseWrapper } from "../../utils/pw";
export class PubSub {
    constructor(gluePromise, asyncConstructors, loggerClient) {
        this.gluePromise = gluePromise;
        this.asyncConstructors = asyncConstructors;
        this.loggerClient = loggerClient;
        this.lastSubId = 0;
        this.unsubscribeFunctions = {};
        this.catalogue = {};
        this.gate = new Gate();
        this.alreadyRegisteredWithIOCD = false;
        this.responders = [];
        const pw = asyncConstructors.addConstructor();
        gluePromise.then((g) => {
            this.glue = g;
            this.initialize();
            asyncConstructors.finishConstructor(pw);
        });
    }
    logStatus(method, type, data) {
        logStatus(this.loggerClient, this.glue, {
            api: "Router.pubsub",
            method, type, data
        });
    }
    async initialize() {
        await this.glue.contexts.subscribe("finsemble-pubsub-catalogue", (data) => {
            this.catalogue = data;
        });
    }
    getLocalResponder(topic) {
        const exactTopicResponder = this.responders.find((responder) => responder.topic === topic);
        const regexTopicResponder = this.responders.find((responder) => {
            return (new RegExp(responder.topic)).test(topic);
        });
        return exactTopicResponder ?? regexTopicResponder;
    }
    handleIncomingPublish(responder, request, caller) {
        if (!responder.publishCallback) {
            responder.updateCallback?.(request.topic, request.data);
            return;
        }
        responder.publishCallback(null, {
            data: request.data,
            header: request.header,
            options: request.options,
            originatedHere: () => caller.peerId === this.glue.interop.instance.peerId,
            sendNotifyToAllSubscribers: (err, data) => {
                if (err) {
                    return;
                }
                responder.updateCallback?.(request.topic, data);
            }
        });
    }
    handleIncomingSubscribe(responder, args, caller) {
        const pw = new PromiseWrapper();
        if (!responder.subscribeCallback) {
            return Promise.resolve({
                data: undefined,
                initialData: responder.initialState ?? {}
            });
        }
        responder.subscribeCallback(null, {
            data: args.data,
            header: args.header,
            options: args.options,
            originatedHere: () => caller.peerId === responder.peerId,
            sendNotifyToSubscriber: (err, data) => {
                pw.resolve({ error: err ? err : undefined, data, initialData: responder.initialState });
            }
        });
        return pw.promise;
    }
    handleIncomingUnsubscribe(responder, args, caller) {
        const pw = new PromiseWrapper();
        if (!responder.unsubscribeCallback) {
            return Promise.resolve({
                data: undefined,
                initialData: responder.initialState ?? {}
            });
        }
        responder.unsubscribeCallback(null, {
            data: args.data,
            header: args.header,
            options: args.options,
            originatedHere: () => caller.peerId === responder.peerId,
            removeSubscriber: () => {
                console.debug("removeSubscriber not implemented in iocd polyfill. Unsubscribe has already occurred.");
            }
        });
        return pw.promise;
    }
    async registerResponder(topic, params, initialState) {
        const topicAsString = (topic instanceof RegExp) ? topic.source : topic;
        if (this.responders.find((r) => topicAsString === r.topic)) {
            throw new Error(`Router:registerResponder(): A local responder for topic already exists:'${topicAsString}'`);
        }
        this.responders.push({
            topic: topicAsString,
            initialState,
            updateCallback: (topic, data) => {
                const contextType = `finsemble-pubsub-topic-${topic}`;
                this.glue.contexts.set(contextType, data);
            },
            subscribeCallback: params?.subscribeCallback,
            unsubscribeCallback: params?.unsubscribeCallback,
            publishCallback: params?.publishCallback,
            peerId: this.glue.interop.instance.peerId ?? "Unknown peer"
        });
        if (!this.alreadyRegisteredWithIOCD) {
            await this.glue.interop.register("finsemble-pubsub-responder", safeInteropRegister(async (args, caller) => {
                const responder = this.getLocalResponder(args.topic);
                if (!responder)
                    return;
                if (args.action === "publish") {
                    this.handleIncomingPublish(responder, args, caller);
                }
                else if (args.action === "subscribe") {
                    return this.handleIncomingSubscribe(responder, args, caller);
                }
                else if (args.action === "unsubscribe") {
                    return this.handleIncomingUnsubscribe(responder, args, caller);
                }
            }));
            this.alreadyRegisteredWithIOCD = true;
        }
        const peerId = this.glue.interop.instance.peerId ?? "Unknown peer";
        const topics = (this.catalogue[peerId] ?? []).concat({
            topic: topicAsString,
            isRegex: topic instanceof RegExp,
            peerId
        });
        const { err } = await toGoLang(() => this.glue.contexts.update("finsemble-pubsub-catalogue", { [peerId]: topics }));
        return { err };
    }
    findRemoteResponder(topic) {
        const isActive = (peerId) => this.glue.interop.servers().find((s) => s.peerId === peerId);
        const deadResponders = Object.keys(this.catalogue).filter((peerId) => !isActive(peerId));
        if (deadResponders) {
            let removeFromCatalogue = {};
            deadResponders.forEach((peerId) => {
                removeFromCatalogue[peerId] = null;
                delete this.catalogue[peerId];
            });
            this.glue.contexts.update("finsemble-pubsub-catalogue", removeFromCatalogue);
        }
        const exactTopicPeerId = Object.keys(this.catalogue).find((peerId) => this.catalogue[peerId].find(t => !t.isRegex && t.topic === topic));
        const regexTopicPeerId = Object.keys(this.catalogue).find((peerId) => this.catalogue[peerId].find(t => t.isRegex && (new RegExp(t.topic)).test(topic)));
        return exactTopicPeerId ?? regexTopicPeerId;
    }
    subscribe(topic, notifyCallback) {
        this.logStatus("subscribe", "start", { topic });
        const subscribeID = this.lastSubId++ + "";
        const subscribeCore = async () => {
            await this.asyncConstructors.ready();
            const peerId = this.findRemoteResponder(topic);
            const contextType = `finsemble-pubsub-topic-${topic}`;
            let finishedSubscribing = false;
            const unsub = await this.glue.contexts.subscribe(contextType, (data, delta, remove, u, extraData) => {
                if (!finishedSubscribing) {
                    return;
                }
                const sr = {
                    data: data?.envelope ?? data,
                    header: {
                        topic,
                        type: "",
                        origin: "",
                        subscribeID: "",
                    },
                    originatedHere: () => !extraData.updateId
                };
                notifyCallback(undefined, sr);
            });
            let response = null;
            if (peerId) {
                const result = await this.glue.interop.invoke("finsemble-pubsub-responder", {
                    action: "subscribe",
                    topic,
                    options: {},
                    header: {}
                }, { peerId }, { waitTimeoutMs: 30000 });
                response = result.returned;
                if (response.error) {
                    unsub();
                    notifyCallback(response.error, {});
                    return;
                }
            }
            this.unsubscribeFunctions[subscribeID] = unsub;
            let currentTopicValue = await this.glue.contexts.get(contextType);
            if (Object.keys(currentTopicValue).length === 0) {
                currentTopicValue = undefined;
            }
            const initialData = response?.data ?? currentTopicValue ?? response?.initialData ?? this.getLocalResponder(topic)?.initialState;
            notifyCallback(undefined, {
                data: initialData?.envelope ?? initialData ?? {},
                header: {
                    topic,
                    type: "",
                    origin: "",
                    subscribeID: "",
                },
                originatedHere: () => false
            });
            finishedSubscribing = true;
        };
        subscribeCore();
        this.logStatus("subscribe", "return");
        return {
            subscribeID,
            topic
        };
    }
    unsubscribe(subscribeIDStruct) {
        this.logStatus("unsubscribe", "start", { subscribeIDStruct });
        const unsubFunction = this.unsubscribeFunctions[subscribeIDStruct.subscribeID];
        if (unsubFunction) {
            unsubFunction();
        }
        else {
            setTimeout(() => {
                this.unsubscribe(subscribeIDStruct);
            }, 100);
        }
        this.logStatus("unsubscribe", "return");
    }
    async publish(topic, event) {
        this.logStatus("publish", "start", { topic });
        await this.asyncConstructors.ready();
        await this.gate.wait(async () => {
            let peerId = this.findRemoteResponder(topic);
            const data = { envelope: event, transactionID: uuid() };
            if (peerId) {
                this.glue.interop.invoke("finsemble-pubsub-responder", {
                    data,
                    action: "publish",
                    topic,
                    options: {},
                    header: {}
                }, { peerId });
            }
            else {
                const contextType = `finsemble-pubsub-topic-${topic}`;
                this.glue.contexts.set(contextType, data);
            }
        });
        this.logStatus("publish", "return");
    }
    addPubSubResponder(topic, initialState, params, callback) {
        this.logStatus("addPubSubResponder", "start", { topic });
        const addPubSubResponderCore = async () => {
            await this.asyncConstructors.ready();
            const { err } = await this.registerResponder(topic, params ?? {}, { envelope: initialState });
            if (err) {
                this.logStatus("addPubSubResponder", "error", { topic, err });
            }
            callback?.(undefined, "success");
            this.logStatus("addPubSubResponder", "return");
        };
        return addPubSubResponderCore();
    }
    async removePubSubResponder(topic) {
        this.logStatus("removePubSubResponder", "start", { topic });
        this.responders = this.responders.filter((r) => r.topic !== topic);
        const data = await this.glue.contexts.get("finsemble-pubsub-catalogue");
        const peerId = this.glue.interop.instance.peerId ?? "Unknown peer";
        const responders = (data[peerId] ?? []).filter((pd) => pd.topic !== topic);
        const { err } = await toGoLang(() => this.glue.contexts.update("finsemble-pubsub-catalogue", { [peerId]: responders }));
        if (err) {
            this.logStatus("removePubSubResponder", "error", { err });
        }
        this.logStatus("removePubSubResponder", "return");
    }
    trustedMessage(incomingMessage) {
        return true;
    }
    async disconnectAll() {
        this.logStatus("disconnectAll", "start");
        await this.asyncConstructors.ready();
        Object.values(this.unsubscribeFunctions).forEach((func) => func());
        this.unsubscribeFunctions = {};
        const peerId = this.glue.interop.instance.peerId ?? "Unknown peer";
        await toGoLang(() => this.glue.contexts.update("finsemble-pubsub-catalogue", { [peerId]: null }));
        await this.glue.interop.unregister("finsemble-pubsub-responder");
        this.responders = [];
        this.logStatus("disconnectAll", "return");
    }
    calibrateTimeWithRouterService(callback) {
        callback(0);
    }
}
